import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import AuthServices from '../../../services/auth.service';
import { toast } from 'react-toastify';
import cookies from '../../../utils/cookies';
import { translateErrorMessage } from '../../../utils';

const initialState = {
  user: {},
  refreshToken: '',
  accessToken: '',
  status: 'idle',
  logoutStatus: 'idle',
  accessTokenVerified: false,
};

export const connectToWalletAction = createAsyncThunk(
  'auth/walletConnect',
  async (body, { dispatch }) => {
    dispatch(slice.actions.handleStatus('pending'));
    try {
      let response = await AuthServices.connectToWallet(body);
      cookies.set('accessToken', response?.data.data.tokens.accessToken, {
        path: '/',
      });
      dispatch(slice.actions.handleStatus('success'));
      return response?.data;
    } catch (error) {
      dispatch(slice.actions.handleStatus('error'));
      toast.error(`Wallet connection: ${translateErrorMessage(error)}`);
    }
  },
);

export const verifyAction = createAsyncThunk('auth/verify', async (data, { dispatch }) => {
  dispatch(slice.actions.handleAccessTokenVerified(false));
  dispatch(slice.actions.handleStatus('pending'));
  try {
    const accessToken = cookies.get('accessToken');
    if (!accessToken) {
      dispatch(slice.actions.handleAccessTokenVerified(true));
      return dispatch(slice.actions.handleStatus('idle'));
    }
    const response = await AuthServices.verify();
    dispatch(slice.actions.handleStatus('success'));
    return response.data;
  } catch (error) {
    toast.error(translateErrorMessage(error));
    cookies.remove('accessToken', { path: '/' });
    dispatch(slice.actions.handleStatus('error'));
    return error?.response?.data;
  }
});

export const disconnectWalletAction = createAsyncThunk(
  'auth/signout',
  async (data, { getState, dispatch }) => {
    dispatch(slice.actions.handleLogoutStatusStatus('pending'));
    // Always clear local auth status first so sticky 'error' cannot loop disconnect effects.
    dispatch(slice.actions.handleStatus('idle'));
    if (!getState().auth.user?._id) {
      return { clearedLocalOnly: true };
    }
    try {
      const response = await AuthServices.logout();
      cookies.remove('accessToken', { path: '/' });
      dispatch(slice.actions.handleLogoutStatusStatus('success'));
      return response.data;
    } catch (error) {
      toast.error(translateErrorMessage(error));
      cookies.remove('accessToken', { path: '/' });
      dispatch(slice.actions.handleLogoutStatusStatus('error'));
      return error?.response?.data;
    }
  },
);

export const updateProfileAction = createAsyncThunk(
  'auth/updateprofile',
  async (body, { dispatch }) => {
    dispatch(slice.actions.handleStatus('pending'));
    try {
      let response = await AuthServices.updateProfile(body);
      dispatch(slice.actions.handleStatus('success'));
      toast.success(response?.data?.message || 'Success');
      return response?.data;
    } catch (error) {
      dispatch(slice.actions.handleStatus('error'));
      toast.error(translateErrorMessage(error));
    }
  },
);

export const setUsernameAction = createAsyncThunk(
  'auth/setusername',
  async (body, { dispatch }) => {
    dispatch(slice.actions.handleStatus('pending'));
    try {
      const response = await AuthServices.setUsername(body);
      dispatch(slice.actions.handleStatus('success'));
      toast.success(response?.data?.message || 'Username set');
      return response?.data;
    } catch (error) {
      dispatch(slice.actions.handleStatus('error'));
      toast.error(translateErrorMessage(error));
      throw error;
    }
  },
);

export const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    handleStatus: (state, action) => {
      state.status = action.payload;
    },
    handleLogoutStatusStatus: (state, action) => {
      state.logoutStatus = action.payload;
    },
    handleAccessTokenVerified: (state, action) => {
      state.accessTokenVerified = action.payload;
    },
    handleUser: (state, action) => {
      action.payload.user.role_code =
        action.payload.user?.role?.code || action.payload.user?.roleId?.code;
      return {
        ...state,
        user: action.payload.user,
        refreshToken: action.payload.tokens.refreshToken,
        accessToken: action.payload.tokens.accessToken,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(connectToWalletAction.fulfilled, (state, action) => {
      if (state.status !== 'success') return;
      const data = action?.payload?.data;
      if (data?.tokens) {
        state.user = data?.user || {};
        state.refreshToken = data?.tokens?.refreshToken || '';
      }
    });

    builder.addCase(verifyAction.fulfilled, (state, action) => {
      if (state.status !== 'success') return;
      const data = action?.payload?.data;
      if (data) {
        state.user = data?.user || {};
        state.refreshToken = data?.tokens?.refreshToken || '';
      }
      state.accessTokenVerified = true;
    });
    builder.addCase(disconnectWalletAction.fulfilled, (state) => {
      // Always clear local session — including when logout was a no-op (no user)
      // or failed — so sticky status === 'error' cannot re-trigger disconnect loops.
      state.user = null;
      state.accessToken = {};
      state.refreshToken = {};
      state.status = 'idle';
      state.accessTokenVerified = true;
      state.logoutStatus = 'idle';
    });
    builder.addCase(updateProfileAction.fulfilled, (state, action) => {
      if (state.status !== 'success') return;
      const data = action?.payload?.data;
      state.user = data;
    });
    builder.addCase(setUsernameAction.fulfilled, (state, action) => {
      if (state.status !== 'success') return;
      const data = action?.payload?.data;
      if (data) state.user = data;
    });
  },
});

export const { handleStatus } = slice.actions;

export default slice.reducer;
